// TODO: Write proper definitions for all prop types.
/* eslint-disable react/prop-types */
import React, { useEffect, useMemo } from 'react';
import Cookies from 'universal-cookie';
import { Provider } from 'react-redux';
import { useReportWebVitals } from 'next/web-vitals';
import { wrapper } from '../store/store';
import CNETApplication from '../apps/cnet_app';
import GiftsApplication from '../apps/gifts_app';
import {
  sessionStorageGetItem,
  sessionStorageRemoveItem,
  sessionStorageSetItem,
} from '../utils/web-storage';
import { webVitalsHandler } from '../utils/web-vitals';
import { cookieBotConsentExpirationCheck } from '../utils/cookiebot';
import { pushPageview as gtmPushPageview, setSessionId } from '../utils/gtm';
import { clonePrevention } from '../utils/clonePrevention';
import {
  FRONTEND_CNET_URL,
  FRONTEND_GIFTS_URL,
  DEFAULT_COUNTRY_CODE,
  PAYMENT_SECRET_HEADER_NAME,
  ENVIRONMENT,
  ROLLBAR_CLIENT_ACCESS_TOKEN,
  CSP_CENTRAL_ENDPOINT_ID,
} from '../utils/constants';
import { buildCSPString } from '../utils/csp';
import routerEvents from 'next-router-events';
import fetch from '../utils/fetch';
import { ErrorBoundary, Provider as RollbarProvider } from '@rollbar/react';
import ErrorOverlay from '../components/01_atoms/ErrorOverlay';
import ErrorPage from './_error';
import rollbar from '../utils/rollbar-agent';

import '../components/01_atoms/PageTransitionBar';
import '../components/01_atoms/PageTransitionBar/_style.scss';
import '../components/01_atoms/CookieBot/style.scss';

// Rollbar options.
const { options: rollbarClientConfigs } = require('../utils/rollbar-agent');

// Internal debugging.
const debug = require('debug')('cw:_app.js');

// To prevent useReportWebVitals from triggering multiple times due to changing function references,
// it's defined OUTSIDE the component.
const logWebVitals = (metric) => {
  // Currently Web Vitals library (https://github.com/GoogleChrome/web-vitals)
  // which is used under the hood in next.js reportWebVitals method,
  // can't really count LCP for video. Most likely it is so because it is not
  // supported by browsers Performance API, see
  // https://developer.mozilla.org/en-US/docs/Web/API/LargestContentfulPaint,
  // so we do not sending such values at this moment.
  if (
    metric.name === 'LCP' &&
    metric.value === 0 &&
    metric.entries &&
    metric.entries[0] &&
    metric.entries[0].element &&
    metric.entries[0].element.tagName === 'VIDEO'
  ) {
    return;
  }

  webVitalsHandler({ ...metric, label: 'web-vital' });
};

const Application = (rest) => {
  const { store, props } = wrapper.useWrappedStore(rest);
  let App;

  // We need to memorize nonce value to avoid re-rendering of scripts
  // during Client routing.
  const memorizedNonce = useMemo(() => props.nonce, []);

  if (props.app === 'gifts') {
    App = GiftsApplication;
  } else {
    App = CNETApplication;
  }

  useReportWebVitals(logWebVitals);

  useEffect(() => {
    // Clean up locally stored web vitals on each page load.
    // Basically, it covers the case when user reloads the page before
    // accepting cookies, or visits policies pages by links in cookie popup.
    const localEvents = sessionStorageGetItem('cw_web_vitals');
    if (localEvents) {
      sessionStorageRemoveItem('cw_web_vitals');
    }
    // Check this is not a cloned site
    clonePrevention(ROLLBAR_CLIENT_ACCESS_TOKEN, ENVIRONMENT);

    // Force "test payment" if the query string includes the special property.
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has(PAYMENT_SECRET_HEADER_NAME)) {
      sessionStorageSetItem(
        PAYMENT_SECRET_HEADER_NAME,
        searchParams.get(PAYMENT_SECRET_HEADER_NAME),
      );

      // Redirect to the same page, but without PAYMENT_SECRET_HEADER_NAME
      // to apply new payment mode.
      const url = new URL(window.location.href);
      url.searchParams.delete(PAYMENT_SECRET_HEADER_NAME);
      props.router.replace(url);
    }

    // Check for "utm_source" if the query string includes the special property.
    if (searchParams.has('utm_source')) {
      const utmSource = searchParams.get('utm_source').toLowerCase();
      let platform = '';

      if (utmSource.includes('facebook') || utmSource === 'meta') {
        platform = 'facebook';
      } else if (utmSource.includes('twitter') || utmSource === 't.co') {
        platform = 'twitter';
      } else if (utmSource.includes('linkedin')) {
        platform = 'linkedin';
      } else if (utmSource.includes('whatsapp')) {
        platform = 'whatsapp';
      }

      if (platform) {
        sessionStorageSetItem('cw_social_media_platform', platform);
      }
    }

    // Restore GA Session value. Used for the cases when a user was redirected to the
    // payment gateway, and returned back. The session must be the same to keep the
    // analytics data consistently.
    if (searchParams.has('ga_custom_sid')) {
      setSessionId(searchParams.get('ga_custom_sid'));
    }

    // To prevent usage the cached version of frontend application by a user, the following
    // code compares the latest version number and version that currently used by the user.
    // If a new version is available, then the page will be reloaded and a user will have
    // the latest version of the app.
    // Currently, there is a delay between new build is available on the server and time when
    // Cloudflare cache will be purged (~5min delay). During that time users receive the old version
    // of the application from Cloudflare cache. Direct request to build.txt will contain a new
    // APP_BUILD_ID, therefore reload will happen for such users. As soon as the reload happens,
    // the user still receives the old version from Cloudflare cache and the process can stack
    // in a loop. To avoid the loop we allow checking APP_BUILD_ID only once withing 5 mins.
    const lastReload = sessionStorageGetItem('app_last_force_reload') ?? 0;
    if (Date.now() - lastReload > 5 * 60 * 1000) {
      const host = props.app === 'gifts' ? FRONTEND_GIFTS_URL : FRONTEND_CNET_URL;
      fetch(`${host}/build.txt`, {
        throwNotOk: false,
      }).then((resp) => {
        if (resp.statusCode === 200) {
          const buildIdEnv = props.appBuildId;
          // Force update the page if new build is available.
          if (buildIdEnv && buildIdEnv !== resp.body) {
            window.location.replace(window.location.href);
            sessionStorageSetItem('app_last_force_reload', Date.now());
          }
        }
      });
    }

    // Add a page listener to automatically delete Cookiebot consent
    // older than 6 months.
    cookieBotConsentExpirationCheck();
    // Add a Cookiebot Consent event listener to report locally stored metrics.
    window.addEventListener('CookiebotOnAccept', webVitalsHandler);
    // Listening "on decline" event as well since it is fired when user accepts
    // only necessary cookies. It needs to remove locally stored data
    // immediately.
    window.addEventListener('CookiebotOnDecline', webVitalsHandler);

    // We can't track page views very well by GTM in SPA, so
    // we have to send custom pageview event on every routeChangeComplete.
    gtmPushPageview();
    routerEvents.on('routeChangeComplete', gtmPushPageview);

    return () => {
      // Add a Cookiebot Consent event listener for Cookiebot consent.
      window.removeEventListener('CookiebotOnAccept', webVitalsHandler);
      window.removeEventListener('CookiebotOnDecline', webVitalsHandler);

      routerEvents.off('routeChangeComplete', gtmPushPageview);
    };
  }, []);

  // Display the error message if an exception caused on the server level.
  // It should be called exactly here to catch all exceptions from all components
  // in CNET / Gifts apps. Before it was displayed directly in CNET/Gifts Layout,
  // and exceptions in some components (placed directly in layouts) could
  // cause "Internal Server Error" exception and only this string was displayed on
  // the page. Currently, in the case of any exception on the server level, it will
  // be handled properly.
  if (props.isServerErr) {
    return <ErrorPage app={props.app} />;
  }

  return (
    <RollbarProvider config={rollbarClientConfigs}>
      <ErrorBoundary
        level="error"
        fallbackUI={() => <ErrorOverlay statusCode={500} app={props.app} />}
      >
        <Provider store={store}>
          <App {...props} nonce={memorizedNonce} />
        </Provider>
      </ErrorBoundary>
    </RollbarProvider>
  );
};

Application.getInitialProps = wrapper.getInitialPageProps((store) => async (props) => {
  const {
    ctx: { res, req, query, err },
  } = props;

  let initialProps = {};
  let App;

  if (
    (typeof window !== 'undefined' && window.location.host.startsWith('gifts')) ||
    (props.ctx?.req?.headers.host && props.ctx.req.headers.host.startsWith('gifts'))
  ) {
    App = GiftsApplication;
    initialProps.app = 'gifts';
  } else {
    App = CNETApplication;
    initialProps.app = 'cnet';
  }

  // Push server error to the Rollbar.
  if (err) {
    rollbar.error(err, {
      path: req ? req.originalUrl : props.ctx.asPath,
      host:
        initialProps.app === 'cnet'
          ? process.env.FRONTEND_CNET_URL
          : process.env.FRONTEND_GIFTS_URL,
    });
    initialProps.isServerErr = true;
  }

  initialProps.testSuite = false;
  let cookie;
  if (req) {
    cookie = new Cookies(req.cookies);
  } else {
    cookie = new Cookies(document.cookies);
  }

  // Determine environment for GTM.
  if (ENVIRONMENT === 'production') {
    const PAYMENT_SECRET_HEADER_VALUE = process.env.PAYMENT_SECRET_HEADER_VALUE;

    // On live site we don't want to collect statistics in live GA
    // from users who are manage the site. In this case we use
    // development GTM environment if user has 'isAdmin' cookie.
    // Also we set `isAdmin` cookie when user use test payment methods
    // on the live site, so GTM will be automatically switched to dev mode.
    /** @link https://www.pivotaltracker.com/story/show/170827963 */
    if (
      PAYMENT_SECRET_HEADER_NAME &&
      PAYMENT_SECRET_HEADER_VALUE &&
      query[PAYMENT_SECRET_HEADER_NAME] &&
      query[PAYMENT_SECRET_HEADER_NAME] === PAYMENT_SECRET_HEADER_VALUE
    ) {
      // Enable development GTM for test payments.
      initialProps.GTMDevMode = true;
      res.cookie('isAdmin', true, {
        expires: new Date(Date.now() + 1000 * 24 * 60 * 60 * 1000),
      });
    } else {
      // Enable development GTM for users who has 'isAdmin' cookie.
      // Enable production GTM for other users.
      initialProps.GTMDevMode = cookie.get('isAdmin') === 'true';
    }
  } else {
    // Enable development GTM for non-production environments.
    initialProps.GTMDevMode = true;
  }

  initialProps.isPreviewMode = !!query.previewMode;

  try {
    // Merge original props with props returned from page component.
    const childInitialProps = await App.getInitialProps({
      store,
      ...props,
    });
    initialProps = { ...initialProps, ...childInitialProps };
  } catch (e) {
    initialProps.statusCode = 502;
    debug(`Error during getting initial props from ${initialProps.app} application. Error: %O`, e);
  }

  // Setting test suite mode by cookie or URL parameter.
  // It allows disabling 3rd-party scripts loading during tests.
  const testSuiteCookie = cookie.get('cw_test_suite');
  if (query['test-suite'] === 'true' || testSuiteCookie) {
    initialProps.testSuite = true;
    if (!testSuiteCookie && res) {
      res.cookie('cw_test_suite', true);
    }
  }

  if (res) {
    // Avoid "Clickjacking" attack on cnet and gifts websites.
    res.setHeader('X-Frame-Options', 'SAMEORIGIN');
    res.setHeader('Content-Security-Policy', "frame-ancestors 'self';");
    const crypto = require('crypto');
    let nonce = crypto.randomBytes(16).toString('base64');
    initialProps.nonce = nonce;
    // Make nonce available to _document.js.
    res.cw_nonce = nonce;
    initialProps.appBuildId = process.env.APP_BUILD_ID ?? 'unknown';
    if (process.env.NODE_ENV === 'production') {
      const userAgent = req?.headers['user-agent'] || '';
      // When ENVIRONMENT=development we want to enforce so CI catches breaks.
      // Live prod: report-only until go-live (flip this string to enforce).
      const cspHeader =
        ENVIRONMENT === 'development'
          ? 'Content-Security-Policy'
          : 'Content-Security-Policy-Report-Only';
      res.setHeader(
        cspHeader,
        buildCSPString(
          nonce,
          CSP_CENTRAL_ENDPOINT_ID,
          userAgent,
          DEFAULT_COUNTRY_CODE,
          ENVIRONMENT,
          FRONTEND_CNET_URL,
        ),
      );
    }

    // Enforce "Strict transport security".
    let strictTransportSecurity = 'max-age=31536000; includeSubDomains';

    // Add "preload" option only on Live domains.
    if (['concern.net', 'concern.org.uk'].some((domain) => FRONTEND_CNET_URL.endsWith(domain))) {
      strictTransportSecurity += '; preload';
    }

    res.setHeader('Strict-Transport-Security', strictTransportSecurity);
  }
  return initialProps;
});

export default Application;
